import { useCallback, useEffect, useRef, useState } from 'react';

import { IDENTITY_ERROR_CODES, isIdentityApiError } from '@identity/application/errors';
import { getAccountUseCases } from '@identity/composition';
import type { AccountRequirement } from '@identity/domain/types';
import {
  VERIFICATION_NETWORK_NOTICE_THRESHOLD,
  VERIFICATION_POLL_INTERVAL_MS,
  VERIFICATION_TIMEOUT_MS,
} from '@identity/presentation/constants';
import { useIdentityFlow } from '@identity/presentation/context';
import { nextStepFor } from '@identity/presentation/utils';

export type UseRequirementVerificationPollingParams = {
  /** The requirement this wait is watching. It belongs to the step, not to the wizard's order. */
  requirement: AccountRequirement;
  /**
   * Any value whose change starts a new wait. It is not the destination: resending to the same one
   * must also reset the wait, and that goes through `restart`.
   */
  resetKey: string;
  /** Called once, when the backend reports the requirement satisfied. */
  onVerified: () => void;
  /**
   * The verification to resolve the query against. With it the requirement stays pending until that
   * link is opened, which is what lets a wait watch a value the account already has verified.
   */
  verificationUuid?: string;
  /**
   * Called once when a later send replaced the verification being watched. Without it that code is
   * counted as one more failure, which is the behaviour of the flows that name no verification.
   */
  onSuperseded?: () => void;
};

export type UseRequirementVerificationPollingResult = {
  hasPersistentNetworkError: boolean;
  isTimedOut: boolean;
  restart: () => void;
};

/**
 * Waits for an action that happens outside this screen: the link is opened on the phone or in an
 * inbox, so the token that verifies the value never reaches the wizard. It asks for the account
 * status again with the credential it already has.
 *
 * The rules of the wait:
 *
 * - It does not poll in the background. A hidden tab skips every tick, and a tab that becomes
 *   visible asks immediately instead of waiting out an interval.
 * - Unmounting stops it and cancels the request in flight.
 * - It stops after a time limit, and only a resend starts it again.
 * - A network error never restarts it: it counts, shows a notice past the threshold, and continues.
 * - The step is derived again every cycle, so this screen cannot fall out of sync with the wizard.
 *
 * A wait has three endings and not two. The third one is a verification a later send replaced, and
 * it stops the polling instead of counting as a failure: retrying would ask about the same dead
 * verification forever.
 *
 * It writes nothing to the cache and logs nothing: the use case already reports every query, and
 * `onVerified` is what tells the wizard to refresh.
 */
export const useRequirementVerificationPolling = ({
  requirement,
  resetKey,
  onVerified,
  verificationUuid,
  onSuperseded,
}: UseRequirementVerificationPollingParams): UseRequirementVerificationPollingResult => {
  const flow = useIdentityFlow();
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [cycle, setCycle] = useState(0);

  // Through a ref, so a new callback on every render of the step does not restart the wait.
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;
  const onSupersededRef = useRef(onSuperseded);
  onSupersededRef.current = onSuperseded;

  const restart = useCallback(() => {
    setCycle((current) => current + 1);
  }, []);

  // A new uuid is a new wait, with no `restart` call. The flows that name none fall back to the key
  // they already used, so their behaviour is unchanged.
  const waitKey = verificationUuid ?? resetKey;

  useEffect(() => {
    let isStopped = false;
    let controller: AbortController | null = null;

    setIsTimedOut(false);
    setConsecutiveFailures(0);

    const stop = () => {
      isStopped = true;
      controller?.abort();
      window.clearInterval(intervalId);
    };

    const isSuperseded = (error: unknown) =>
      Boolean(onSupersededRef.current) &&
      isIdentityApiError(error) &&
      error.code === IDENTITY_ERROR_CODES.VERIFICATION_SUPERSEDED;

    const poll = async () => {
      // Paused, not stopped: nobody is watching a hidden tab.
      if (isStopped || document.visibilityState === 'hidden') return;

      controller = new AbortController();

      try {
        const status = await getAccountUseCases(flow).getAccountStatus.execute({
          signal: controller.signal,
          verificationUuid,
        });
        if (isStopped) return;

        setConsecutiveFailures(0);

        if (nextStepFor([requirement], status.requirements) !== requirement) {
          stop();
          onVerifiedRef.current();
        }
      } catch (error) {
        if (isStopped) return;

        if (isSuperseded(error)) {
          stop();
          onSupersededRef.current?.();

          return;
        }

        setConsecutiveFailures((current) => current + 1);
      }
    };

    const intervalId = window.setInterval(poll, VERIFICATION_POLL_INTERVAL_MS);

    const timeoutId = window.setTimeout(() => {
      stop();
      setIsTimedOut(true);
    }, VERIFICATION_TIMEOUT_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    // The first query, with no delay: `visibilitychange` never fires for a tab that is already
    // visible, so it would otherwise wait out a whole interval.
    poll();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearTimeout(timeoutId);
      stop();
    };
  }, [cycle, flow, requirement, verificationUuid, waitKey]);

  return {
    hasPersistentNetworkError: consecutiveFailures >= VERIFICATION_NETWORK_NOTICE_THRESHOLD,
    isTimedOut,
    restart,
  };
};
